import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Grid,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Update as UpdateIcon,
} from '@mui/icons-material';
import { generateSchemaName } from '../../utils/schemaNameGenerator';
import { ColumnMappingData, SchemaData } from '../../pages/ExcelUploadPage';
import {
  DEFAULT_SETTINGS,
  isDuplicateSchemaName,
  SaveModeToggle,
  SchemaNameField,
} from './_shared';

interface SchemaSaveDialogProps {
  open: boolean;
  /** Close without processing */
  onClose: () => void;
  /** Save schema then process */
  onSaveAndProcess: (
    schemaName: string,
    settings: SchemaData['settings'],
    existingSchemaId?: string
  ) => void;
  /** Process without saving */
  onProcessWithoutSaving: (settings: SchemaData['settings']) => void;
  schemas: SchemaData[];
  loadedSchema: SchemaData | undefined;
  currentMappings: ColumnMappingData;
  headers?: string[];
  companyCode?: number | string;
  /** Override hasHeader from the mapping screen */
  currentHasHeader?: boolean;
}

export const SchemaSaveDialog: React.FC<SchemaSaveDialogProps> = ({
  open,
  onClose,
  onSaveAndProcess,
  onProcessWithoutSaving,
  schemas,
  loadedSchema,
  currentMappings,
  headers,
  companyCode,
  currentHasHeader,
}) => {
  const [schemaName, setSchemaName] = useState('');
  const [isNameManuallyEdited, setIsNameManuallyEdited] = useState(false);
  const [schemaSettings, setSchemaSettings] =
    useState<SchemaData['settings']>(DEFAULT_SETTINGS);
  const [saveMode, setSaveMode] = useState<'update' | 'new'>('new');

  const generatedName = useMemo(
    () => generateSchemaName(currentMappings, headers, companyCode),
    [currentMappings, headers, companyCode]
  );

  // Seed state every time the dialog opens
  useEffect(() => {
    if (!open) return;
    const baseSettings = loadedSchema?.settings ?? DEFAULT_SETTINGS;
    // Apply currentHasHeader from the mapping screen if provided
    const resolvedSettings = currentHasHeader === undefined
      ? baseSettings
      : { ...baseSettings, hasHeader: currentHasHeader };
    if (loadedSchema) {
      setSaveMode('update');
      setSchemaName(loadedSchema.name);
      setIsNameManuallyEdited(false);
      setSchemaSettings(resolvedSettings);
    } else {
      setSaveMode('new');
      setSchemaName(generatedName);
      setIsNameManuallyEdited(false);
      setSchemaSettings(resolvedSettings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep name in sync with generated name while in "new" mode unless manually edited
  useEffect(() => {
    if (!isNameManuallyEdited && saveMode === 'new') {
      setSchemaName(generatedName);
    }
  }, [generatedName, isNameManuallyEdited, saveMode]);

  const handleSaveModeChange = (mode: 'update' | 'new') => {
    setSaveMode(mode);
    const baseSettings = mode === 'update' && loadedSchema
      ? (loadedSchema.settings ?? DEFAULT_SETTINGS)
      : DEFAULT_SETTINGS;
    const resolvedSettings = currentHasHeader === undefined
      ? baseSettings
      : { ...baseSettings, hasHeader: currentHasHeader };
    if (mode === 'update' && loadedSchema) {
      setSchemaName(loadedSchema.name);
      setIsNameManuallyEdited(false);
    } else {
      setSchemaName(generatedName);
      setIsNameManuallyEdited(false);
    }
    setSchemaSettings(resolvedSettings);
  };

  const isDuplicateName = isDuplicateSchemaName(schemaName, schemas, saveMode, loadedSchema);

  const handleSave = () => {
    const nameToSave = schemaName.trim();
    if (!nameToSave) return;
    const existingId =
      saveMode === 'update' && loadedSchema?.id ? loadedSchema.id : undefined;
    onSaveAndProcess(nameToSave, schemaSettings, existingId);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {loadedSchema ? 'Mapping has changed' : 'Save Schema'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {loadedSchema
            ? 'Save your changes before uploading, or continue without saving.'
            : 'Save as a reusable schema for future uploads.'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Update vs New toggle — only when an existing schema was loaded */}
        {loadedSchema && (
          <SaveModeToggle
            saveMode={saveMode}
            loadedSchemaName={loadedSchema.name}
            onChange={handleSaveModeChange}
          />
        )}

        {/* Schema Name */}
        <SchemaNameField
          schemaName={schemaName}
          isNameManuallyEdited={isNameManuallyEdited}
          isDuplicateName={isDuplicateName}
          onChangeName={v => { setSchemaName(v); setIsNameManuallyEdited(true); }}
          onRevert={() => { setSchemaName(generatedName); setIsNameManuallyEdited(false); }}
          wrapperSx={{ mt: 1, mb: 2 }}
        />

        <Divider sx={{ mb: 3 }} />

        {/* Processing Settings */}
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
          Processing Settings
        </Typography>

        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Header row
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  First row contains column names
                </Typography>
              </Box>
              <Switch
                size="small"
                checked={schemaSettings.hasHeader}
                onChange={e =>
                  setSchemaSettings(prev => ({ ...prev, hasHeader: e.target.checked }))
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Street distances
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Use road distances for trucks
                </Typography>
              </Box>
              <Switch
                size="small"
                checked={schemaSettings.useStreetDistances}
                onChange={e =>
                  setSchemaSettings(prev => ({ ...prev, useStreetDistances: e.target.checked }))
                }
              />
            </Box>
          </Grid>

        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={() => onProcessWithoutSaving(schemaSettings)} variant='contained' color="inherit" sx={{ mr: 'auto' }}>
          Upload without Saving
        </Button>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={saveMode === 'update' ? <UpdateIcon /> : <SaveIcon />}
          disabled={!schemaName.trim()}
          color={isDuplicateName ? 'warning' : 'primary'}
        >
          {(() => {
            if (saveMode === 'update') return 'Save and Upload';
            if (isDuplicateName) return 'Overwrite and Upload';
            return 'Save and Upload';
          })()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
